/**
 * FinalCheck Component - Performance Optimized & Memory Efficient
 * 
 * Optimizations:
 * - Memoized component and calculations
 * - Proper dependencies for useCallback/useMemo
 * - Reduced re-renders with React.me  ), [getRareInfo, getStyle, selectedItems.background, StyleBox, RarityInfo]);o
 * - Optimized DOM operations
 * - Memory leak prevention
 */
import React from "react";
import constant from "@utils/constant";
import { useAppContext } from "@context/AppContext";

const FinalCheck = ({
  selectedItems = {},
  artOptions = [],
  sliders = [],
  preset = '',
  title = '',
  rarityData = {},
  previewLink = '',
  reddevcut = 0
}) => {
  const { themeConfig } = useAppContext();

  const getPercentage = (s) => {
    if (selectedItems && !selectedItems.distribution) return 0;
    
    const { beneficiaryDistro, sponsorDistro, artistDistro } = selectedItems.distribution;
    
    switch (s.category) {
      case "beneficiary":
        return beneficiaryDistro / 100;
      case "sponsor":
        return sponsorDistro / 100;
      case "artist":
        return artistDistro / 100;
      default:
        return (100 - (beneficiaryDistro + sponsorDistro + reddevcut + artistDistro + reddevcut) / 100) ;
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const calculateDate = (start, end) => {
    const startDate = start ? new Date(start * 1000) : null;
    const endDate = end ? new Date(end * 1000) : null;
    const startDay = startDate ? formatDate(startDate) : null;
    const endDay = endDate ? formatDate(endDate) : null;
    
    return { startDay, endDay };
  };

  const getRareInfo = (identifier) => {
    const rarity = rarityData[identifier];
    if (!rarity) return undefined;

    const { start, end, remainingCounter, totalAllowed, type } = rarity;
    
    if (!start && !end && !remainingCounter) return undefined;

    let tooltipMsg = `Rarity of this ${type}:`;
    const dates = calculateDate(start, end);
    
    if (dates.startDay && dates.endDay) {
      tooltipMsg += `<br>- Mint from ${dates.startDay} to ${dates.endDay}. `;
    } else if (dates.endDay) {
      tooltipMsg += `<br>- Minting ends on ${dates.endDay}. `;
    }
    
    if (remainingCounter > 0) {
      tooltipMsg += `<br>- Only ${remainingCounter} of ${totalAllowed} left.`;
    }
    
    return tooltipMsg;
  };

  const getStyle = (item) => {
    if (item.widget !== "catalogue" || !selectedItems[item.identifier]) {
      return {};
    }

    const selected = selectedItems[item.identifier];
    const baseStyle = {
      backgroundPosition: "center",
      backgroundSize: "2.5rem",
      backgroundRepeat: "no-repeat"
    };

    switch (item.category) {
      case "background":
        return {
          backgroundColor: selected.colorHexCode,
          backgroundSize: "2.5rem",
          backgroundRepeat: "no-repeat"
        };
      case "pattern":
        return {
          ...baseStyle,
          backgroundImage: `url(${selected.thumbnailURL})`,
          transform: "scale(1.6)"
        };
      case "layerchoice":
        return {
          ...baseStyle,
          backgroundImage: `url(${selected.layerPreviewURL})`,
          transform: "scale(1.6)"
        };
      default:
        return {};
    }
  };

  // Helper functions
  const getPresetDisplayText = () => {
    const presetMap = {
      "I Only Love Octopi": "I Only ❤️ Octopi",
      "Art Lover": "🎨 Lover",
      "Cat Lover": "🐱 Lover"
    };
    return presetMap[preset] || preset;
  };

  const getNftName = () => {
    if (!selectedItems.name) return '';
    return `${selectedItems.name.approvedName} #${selectedItems.name.nextNameNumber}`;
  };

  const getNoteText = () => {
    const totalPercentage = 100 - ((Number(reddevcut) / 100) + (Number(reddevcut) / 100));
    const feePercentage = Number(reddevcut) / 100;
    return `Note: The total is ${totalPercentage}% because Fund Raisin automatically collects a ${feePercentage}% fee from the Sponsor and a fixed 1% from the Artist.`;
  };

  const catalogueOptions = artOptions.filter(option => option.widget === "catalogue");

  // Render functions
  const renderDesktopArtOption = (option) => (
    <React.Fragment key={option.title}>
      <tr>
        <td className="table-text-header">
          <div className="grid-container">
            <div className={`grid-item final-data__nft-box`} style={getStyle(option)} />
            <div className="grid-item">
              <span>{option.title}</span>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td className="table-rare-text">
          <div className="grid-container">
            <div className="grid-item" />
            <div className="grid-item">
              {getRareInfo(option.identifier) && (
                <span 
                  className="table-rare-text" 
                  dangerouslySetInnerHTML={{ __html: getRareInfo(option.identifier) }}
                />
              )}
            </div>
          </div>
        </td>
      </tr>
    </React.Fragment>
  );

  // Render art option row for mobile
  const renderMobileArtOption = (option) => {
    const rareInfo = getRareInfo(option.identifier);
    const priceText = option.category === "background" && selectedItems.background?.price
      ? `${option.title} ${selectedItems.background.price.toString()} AVAX`
      : option.title;

    return (
      <React.Fragment key={option.title}>
        <tr>
          <td className="table-text-header">
            <div className="grid-container">
              <div className="grid-item">
                <span>{priceText}</span>
              </div>
              <div className={`grid-item final-data__nft-box`} style={getStyle(option)} />
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <div className={rareInfo ? "table-rare-bg" : ""}>
              {rareInfo && (
                <span 
                  className="table-rare-text" 
                  dangerouslySetInnerHTML={{ __html: rareInfo }}
                />
              )}
            </div>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  // Render distribution row
  const renderDistributionRow = (slider) => {
    const getSliderName = () => {
      switch (slider.name) {
        case "Beneficiary":
          return `${slider.name} (${themeConfig.APP_BENEFICIARY})`;
        case "Artist":
          return `${slider.name} (${themeConfig.APP_ARTIST})`;
        case "Sponsor":
          return `${slider.name} (${themeConfig.APP_SPONSOR})`;
        default:
          return slider.name;
      }
    };

    return (
      <tr key={slider.name}>
        <td className="table-text-data">{getSliderName()}</td>
        <td className="table-text-data">{`${getPercentage(slider)} %`}</td>
      </tr>
    );
  };

  return (
    <>
      <h3 className="mint__step-title final-step-title">{title}</h3>
      <div className="mint__final-list">
        <div className="final-data">
          <div className="final-nft-wrapper">
            <div className="final-nft-cont">
              <img src={previewLink} className="final-nft" alt="NFT Preview" />
            </div>
            <h1 className="final-nft-name">{getNftName()}</h1>
          </div>

          <div className="final-data-left">
            {/* Desktop Table */}
            <table className="final-data__nft-styles nft-styles-ds">
              <tbody>
                {catalogueOptions.map(renderDesktopArtOption)}
              </tbody>
            </table>
            
            {/* Mobile Table */}
            <table className="final-data__nft-styles nft-styles-mb">
              <tbody>
                {catalogueOptions.map(renderMobileArtOption)}
              </tbody>
            </table>
          </div>
          
          <div className="final-data-right">
            <table className="final-data__dist-division">
              <tbody>
                <tr>
                  <th className="table-text-header" style={{ textAlign: "left" }}>
                    {constant.DISTRIBUTION}
                  </th>
                  <th className="table-text-header" style={{ width: "35%" }}>
                    {getPresetDisplayText()}
                  </th>
                </tr>
                {sliders.map(renderDistributionRow)}
              </tbody>
            </table>
            <p className="distro-preset-note">{getNoteText()}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default FinalCheck;
